// ProductDetailPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { heIL } from '@mui/x-data-grid-pro/locales';

// Additional imports for audit history
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { format, startOfMonth, endOfMonth } from 'date-fns';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import HomeIcon from '@mui/icons-material/Home';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudinaryUploadWidget from './widgets/CloudinaryUploadWidget';
import InventoryManagementDialog from './dialogs/InventoryManagementDialog';
import BarcodePrintDialog from './dialogs/BarcodePrintDialog';
import MobileDialog from './dialogs/MobileDialog';
import StarIcon from '@mui/icons-material/Star';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { InputAdornment } from '@mui/material';
import { renderBarcodeSVG, isPrintableBarcode } from '../utils/barcodePrintUtils';

const UNSUPPORTED_BARCODE_MSG =
  'ניתן להדפיס רק ברקודים מסוג EAN-13, EAN-8, ITF-14 או Code 128';

const theme = createTheme({
  direction: 'rtl',
  ...heIL,
  typography: {
    fontSize: 16,
  },
});

const fetchProduct = async (productId) => {
  const response = await API.get(`/product/?ProductID=${productId}`);
  return response.data;
};

const fetchStorages = async () => {
  const response = await API.get('/storages/?include_inventory_stats=true');
  return response.data;
};

// Helper function for default date range (1 month back)
function getOneMonthBackRange() {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  const start = new Date(now);
  start.setMonth(start.getMonth() - 1);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

// Audit history toolbar
function CustomAuditToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
    </GridToolbarContainer>
  );
}

const ProductDetailPage = () => {
  const { product_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userProfile } = useUser();
  const role = userProfile?.role;
  
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Inventory dialog state
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [selectedStorage, setSelectedStorage] = useState(null);

  // Barcode acceleration state (view/regenerate/print)
  const barcodeEnabled = userProfile?.group?.barcode_config?.enabled === true;
  const barcodePrintSymbology = userProfile?.group?.barcode_config?.print_symbology;
  const [barcodePrintOpen, setBarcodePrintOpen] = useState(false);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [barcodeBusy, setBarcodeBusy] = useState(false);
  const barcodeSvgRef = useRef(null);
  const [selectedQuantity, setSelectedQuantity] = useState(0);

  // Inventory audit history state with default date range
  const { start, end } = getOneMonthBackRange();
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditSummary, setAuditSummary] = useState({ total_additions: 0, total_reductions: 0 });
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditPaginationModel, setAuditPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [auditRowCount, setAuditRowCount] = useState(0);
  const [auditFilters, setAuditFilters] = useState({
    changeType: 'All',
    storageId: 'All',
    startDate: start,
    endDate: end,
    search: ''
  });

  // UI config
  const uiConfig = userProfile?.ui_config || {};
  const allowOrderManagement = uiConfig.allow_order_management || false;
  const orderConfig = userProfile?.group?.order_config || {};
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled && allowOrderManagement;
  const productFields = uiConfig.product_fields || [];
  const productFieldsSchema = uiConfig.product_fields_schema || {};
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {}; 

  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';

  // Permission roles from config
  const pageConfig = uiConfig.pages_configs?.ProductsPage || {};
  const updateInventoryFullRoles = pageConfig.update_inventory_full_roles || ["manager", "employee"];
  const canUpdateInventoryFull = updateInventoryFullRoles.includes(role);
  // Propagate the same take/add gates ProductsPage uses so the new
  // add/take dialog on ProductDetailPage respects the group's config.
  const canTakeInventory = (pageConfig.inventory_take_roles || ['manager', 'employee']).includes(role);
  const canAddInventory = (pageConfig.inventory_add_roles || ['manager', 'employee']).includes(role);

  // images
  const imagesEnabled = productFields.includes('images');
  const [cloudinaryConfig, setCloudinaryConfig] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const galleryRef = useRef(null);
  const [deleteImageDialogOpen, setDeleteImageDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Cloudinary config state

  // Fields to always exclude from display/edit
  const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup', 
                         'inventory', 'total_quantity', 'default_storage_quantity'];
  
  // Fetch product data
  const {
    data: product,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['product', product_id],
    queryFn: () => fetchProduct(product_id),
    enabled: !!product_id,
  });

  // Fetch storages if storage management is enabled
  const {
    data: storages = [],
    isLoading: isLoadingStorages,
  } = useQuery({
    queryKey: ['storages'],
    queryFn: fetchStorages,
    enabled: storageEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch audit history 
  const fetchAuditHistory = async () => {
    if (!product_id || !storageEnabled) return;
    
    setLoadingAudit(true);
    try {
      // Build params
      const params = {
        ProductID: product_id,
        page: auditPaginationModel.page + 1,
        page_size: auditPaginationModel.pageSize,
      };
      
      // Add filters
      if (auditFilters.changeType !== 'All') {
        params.change_type = auditFilters.changeType;
      }
      if (auditFilters.storageId !== 'All') {  // ADD THIS
        params.StorageID = auditFilters.storageId;
      }
      if (auditFilters.startDate) {
        params.start_date = format(auditFilters.startDate, 'yyyy-MM-dd');
      }
      if (auditFilters.endDate) {
        params.end_date = format(auditFilters.endDate, 'yyyy-MM-dd');
      }
      if (auditFilters.search) {
        params.search = auditFilters.search;
      }
      
      console.log('Fetching audit history with params:', params);
      
      // Fetch paginated audit history
      const historyResponse = await API.get('/inventory/audit-log/', { params });
      console.log('Audit history response:', historyResponse.data);
      
      setAuditHistory(historyResponse.data.results || []);
      setAuditRowCount(historyResponse.data.count || 0);
      
      // Fetch summary (without pagination)
      const summaryParams = { ...params };
      delete summaryParams.page;
      delete summaryParams.page_size;
      const summaryResponse = await API.get('/inventory/audit-summary/', { params: summaryParams });
      setAuditSummary(summaryResponse.data);
      
    } catch (error) {
      console.error('Error fetching audit history:', error);
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => {
    if (imagesEnabled) {
      API.get('/image/config/')
        .then(response => setCloudinaryConfig(response.data))
        .catch(error => console.error('Failed to fetch Cloudinary config:', error));
    }
  }, [imagesEnabled]);

  // Fetch audit history when product loads or pagination changes
  useEffect(() => {
    if (product && storageEnabled) {
      fetchAuditHistory();
    }
  }, [product, storageEnabled, auditPaginationModel.page, auditPaginationModel.pageSize]);

  // Audit history columns - styled like transactions page
  const auditColumns = [
    {
      field: 'Timestamp',
      headerName: 'תאריך ושעה',
      flex: 0.5,
      minWidth: 170,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return new Date(params.value).toLocaleString('he-IL', {
          day: '2-digit',
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      field: 'ChangeType',
      headerName: 'סוג שינוי',
      flex: 0.3,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isAddition = params.value === 'addition';
        return (
          <Chip
            icon={isAddition ? <AddCircleIcon /> : <RemoveCircleIcon />}
            label={isAddition ? 'הוספה' : 'הפחתה'}
            color={isAddition ? 'success' : 'error'}
            size="small"
            variant="outlined"
            sx={{direction: 'ltr'}}
          />
        );
      }
    },
    {
      field: 'StorageName',
      headerName: 'מחסן',
      flex: 0.4,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const storageName = params.value || '';
        const isDefault = params.row.Storage?.is_default;
        const displayText = isDefault ? `${storageName} (ברירת מחדל)` : storageName;
        
        return (
          <Tooltip 
            title={displayText} 
            arrow 
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  color: 'black',
                  fontSize: '1.2rem',
                  maxWidth: 500,
                  padding: '10px 12px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap'
                }
              },
              arrow: {
                sx: {
                  color: 'white',
                  '&::before': {
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }
                }
              }
            }}
          >
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%',
              cursor: 'pointer',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              {storageName}
              {isDefault && (
                <Chip size="small" label="ברירת מחדל" color="primary" sx={{ fontSize: '0.7rem' }} />
              )}
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'QuantityChange',
      headerName: 'כמות שינוי',
      flex: 0.3,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const value = parseFloat(params.value || 0);
        const isPositive = value > 0;
        return (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}>
            <Typography sx={{ 
              fontWeight: 'bold',
              color: isPositive ? 'success.main' : 'error.main',
              fontSize: '1.1rem',
              direction: 'ltr'
            }}>
              {isPositive ? '+' : ''}{value}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'QuantityBefore',
      headerName: 'כמות לפני',
      flex: 0.3,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => parseFloat(value || 0).toFixed(2)
    },
    {
      field: 'QuantityAfter',
      headerName: 'כמות אחרי',
      flex: 0.3,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const value = parseFloat(params.value || 0);
        return (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}>
            <Typography sx={{ 
              fontWeight: 'bold', 
              fontSize: '1.1rem' 
            }}>
              {value.toFixed(2)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'ChangedByFullName',
      headerName: 'בוצע על ידי',
      flex: 0.4,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const displayValue = params.value || 'מערכת';
        return (
          <Tooltip 
            title={displayValue} 
            arrow 
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  color: 'black',
                  fontSize: '1.2rem',
                  maxWidth: 500,
                  padding: '10px 12px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap'
                }
              },
              arrow: {
                sx: {
                  color: 'white',
                  '&::before': {
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }
                }
              }
            }}
          >
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'ChangeReason',
      headerName: 'סיבה',
      flex: 0.5,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const reasonMap = {
          'inventory_receive': 'קבלה',
          'inventory_deduct': 'לקיחה',
          'inventory_count': 'ספירה',
          'manual_adjustment': 'עדכון ידני',
          'order_fulfillment': 'מילוי הזמנה',
          'order_cancelled': 'ביטול הזמנה',
          'initial_stock': 'מלאי התחלתי',
        };
        const translated = reasonMap[params.value] || params.value || '-';
        return (
          <Tooltip
            title={translated}
            arrow
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  color: 'black',
                  fontSize: '1.2rem',
                  maxWidth: 500,
                  padding: '10px 12px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap'
                }
              },
              arrow: {
                sx: {
                  color: 'white',
                  '&::before': {
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }
                }
              }
            }}
          >
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              {translated}
            </div>
          </Tooltip>
        );
      }
    },
    {
      field: 'Notes',
      headerName: 'הערות',
      flex: 0.5,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Tooltip 
          title={params.value || '-'} 
          arrow 
          placement="top"
          enterDelay={500}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'white',
                color: 'black',
                fontSize: '1.2rem',
                maxWidth: 500,
                padding: '10px 12px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap'
              }
            },
            arrow: {
              sx: {
                color: 'white',
                '&::before': {
                  border: '1px solid rgba(0, 0, 0, 0.1)'
                }
              }
            }
          }}
        >
          <div style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            width: '100%',
            cursor: 'pointer',
            textAlign: 'center'
          }}>
            {params.value || '-'}
          </div>
        </Tooltip>
      )
    }
  ];

  // Get fields to display in order
  const getFieldsInOrder = () => {
    const orderedFields = [];
    
    const specialOrderFields = [
      'ProductName',
      'CatalogNumber',
      'Barcode',
      'Price',
      'MinPrice',
      'CostPrice',
      'unit',
      'inventory',
      'totalInventory',
      'Description',
      'actions'
    ];
    
    orderedFields.push('ProductName');
    
    if (productFields.includes('CatalogNumber') && 
        !excludedFields.includes('CatalogNumber') &&
        (!productFieldsSchema.CatalogNumber || !productFieldsSchema.CatalogNumber.readonly)) {
      orderedFields.push('CatalogNumber');
    }
    
    if (productFields.includes('Barcode') && 
        !excludedFields.includes('Barcode') &&
        (!productFieldsSchema.Barcode || !productFieldsSchema.Barcode.readonly)) {
      orderedFields.push('Barcode');
    }
    
    orderedFields.push('Price');

    if (productFields.includes('MinPrice') &&
        !excludedFields.includes('MinPrice') &&
        (!productFieldsSchema.MinPrice || !productFieldsSchema.MinPrice.readonly)) {
      orderedFields.push('MinPrice');
    }

    if (productFields.includes('CostPrice') &&
        !excludedFields.includes('CostPrice') &&
        (!productFieldsSchema.CostPrice || !productFieldsSchema.CostPrice.readonly)) {
      orderedFields.push('CostPrice');
    }

    if (productFields.includes('unit') && 
        !excludedFields.includes('unit') &&
        (!productFieldsSchema.unit || !productFieldsSchema.unit.readonly)) {
      orderedFields.push('unit');
    }
    
    productFields.forEach(fieldName => {
      if (!orderedFields.includes(fieldName) && 
          !excludedFields.includes(fieldName) &&
          !specialOrderFields.includes(fieldName) &&
          productFieldsSchema[fieldName] &&
          !productFieldsSchema[fieldName].readonly) {
        orderedFields.push(fieldName);
      }
    });
    
    if ((productFields.includes('Description') || productFields.length === 0) && 
        !excludedFields.includes('Description') &&
        (!productFieldsSchema.Description || !productFieldsSchema.Description.readonly)) {
      orderedFields.push('Description');
    }
    
    return orderedFields;
  };

  useEffect(() => {
    if (imagesEnabled && !window.cloudinary?.galleryWidget) {
      const script = document.createElement('script');
      script.src = 'https://product-gallery.cloudinary.com/all.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [imagesEnabled]);

  // Initialize form data from product
  useEffect(() => {
    if (product) {
      const fieldsToDisplay = getFieldsInOrder();
      const newData = {};
      
      fieldsToDisplay.forEach(fieldName => {
        if (product[fieldName] !== undefined) {
          newData[fieldName] = product[fieldName]?.toString() || '';
        } else if (product.extra_fields && product.extra_fields[fieldName] !== undefined) {
          newData[fieldName] = product.extra_fields[fieldName]?.toString() || '';
        } else {
          const fieldSchema = productFieldsSchema[fieldName];
          newData[fieldName] = fieldSchema?.default || '';
        }
      });
      
      setFormData(newData);
      setOriginalData(newData);
    }
  }, [product, productFields, productFieldsSchema]);

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: async (updatedProduct) => {
      const response = await API.put('/product/update/', {
        ProductID: product_id,
        ...updatedProduct,
      });
      return response.data;
    },
    onSuccess: () => {
      setFormSuccess(`ה${productLabel} עודכן בהצלחה`);
      setIsEditing(false);
      queryClient.invalidateQueries(['product', product_id]);
      queryClient.invalidateQueries(['products']);
      
      setTimeout(() => {
        setFormSuccess('');
      }, 3000);
    },
    onError: (error) => {
      setFormError(error?.response?.data?.error || `שגיאה בעדכון ה${productLabel}`);
    },
  });

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: async () => {
      const response = await API.post('/product/delete/', { ProductID: product_id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      navigate('/products');
    },
    onError: (error) => {
      setFormError(error?.response?.data?.error || `שגיאה במחיקת ה${productLabel}`);
    },
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSwitchChange = (fieldName, checked) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: checked,
    }));
  };

  const handleSubmit = () => {
    setFormError('');
    
    const fieldsToDisplay = getFieldsInOrder();
    for (const fieldName of fieldsToDisplay) {
      const fieldSchema = productFieldsSchema[fieldName] || {};
      
      if (fieldName === 'ProductName' && (!formData.ProductName || !formData.ProductName.trim())) {
        setFormError(`${fieldSchema.label || 'שם מוצר'} הוא שדה חובה`);
        return;
      }
      
      if (fieldName === 'Price' && (!formData.Price || isNaN(parseFloat(formData.Price)))) {
        setFormError('יש להזין מחיר תקין');
        return;
      }
      
      if (fieldSchema.required && !fieldSchema.readonly && 
          (!formData[fieldName] || formData[fieldName].toString().trim() === '')) {
        setFormError(`${fieldSchema.label || fieldName} הוא שדה חובה`);
        return;
      }
    }
    
    const submitData = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        if (key === 'Price' || productFieldsSchema[key]?.type === 'number') {
          submitData[key] = parseFloat(value);
        } else {
          submitData[key] = value;
        }
      }
    });
    
    updateProductMutation.mutate(submitData);
  };

  const handleResetField = (fieldName) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: originalData[fieldName]
    }));
  };

  const handleResetAllFields = () => {
    setFormData(originalData);
  };

  const handleCancelEdit = () => {
    setFormData(originalData);
    setIsEditing(false);
    setFormError('');
  };

  const handleStartEditing = () => {
    if (product) {
      const fieldsToDisplay = getFieldsInOrder();
      const currentData = {};
      
      fieldsToDisplay.forEach(fieldName => {
        if (product[fieldName] !== undefined) {
          currentData[fieldName] = product[fieldName]?.toString() || '';
        } else if (product.extra_fields && product.extra_fields[fieldName] !== undefined) {
          currentData[fieldName] = product.extra_fields[fieldName]?.toString() || '';
        } else {
          const fieldSchema = productFieldsSchema[fieldName];
          currentData[fieldName] = fieldSchema?.default || '';
        }
      });
      
      setOriginalData(currentData);
      setFormData(currentData);
    }
    
    setIsEditing(true);
    setFormError('');
    setFormSuccess('');
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteProductMutation.mutate();
    setDeleteDialogOpen(false);
  };

  // Inventory handlers
  const handleInventoryEdit = (storage, currentQuantity) => {
    setSelectedStorage(storage);
    setSelectedQuantity(currentQuantity);
    setInventoryDialogOpen(true);
  };

  const handleInventorySuccess = () => {
    queryClient.invalidateQueries(['product', product_id]);
    queryClient.invalidateQueries(['products']);
    setInventoryDialogOpen(false);
    fetchAuditHistory();
  };

  // Add this function with your other handlers in ProductDetailPage
  const handleDeleteImage = async (imageId) => {
    try {
      await API.post('/image/delete/', { image_id: imageId });
      queryClient.invalidateQueries(['product', product_id]);
      queryClient.invalidateQueries(['products']);
    } catch (error) {
      console.error('Delete failed:', error);
      setFormError('שגיאה במחיקת תמונה');
    }
  };

  const handleDownloadImage = async (image, e) => {
    e.stopPropagation();
    
    try {
      // Get the image URL
      let downloadUrl = image.url || image.display_url;
      
      // For Cloudinary images, use fetch to download then create blob
      if (downloadUrl.includes('cloudinary.com')) {
        // Add download flag to Cloudinary URL
        downloadUrl = downloadUrl.replace('/upload/', '/upload/fl_attachment/');
        
        // Fetch the image as blob to avoid CORS issues
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        
        // Create object URL from blob
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create and trigger download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = image.name || `product_${product_id}_image_${image.id}.jpg`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        // For non-Cloudinary images, use direct download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = image.name || `image_${image.id}.jpg`;
        link.target = '_blank';  // Open in new tab if download fails
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(image.url || image.display_url, '_blank');
    }
  };


  const handleSetPrimary = async (imageId) => {
    try {
      await API.post('/image/set-primary/', { image_id: imageId });
      queryClient.invalidateQueries(['product', product_id]);
      queryClient.invalidateQueries(['products']);
    } catch (error) {
      console.error('Set primary failed:', error);
      setFormError('שגיאה בעדכון תמונה ראשית');
    }
  };

  const getOptimizedImageUrl = (url) => {
    if (!url) return '';
    
    // Check if it's already a Cloudinary URL
    if (!url.includes('cloudinary.com')) return url;
    
    // Add transformations for optimal viewing
    // Insert transformations after '/upload/' in the URL
    const transformations = 'f_auto,q_auto:best,c_limit';
    
    // If URL already has transformations (has '/v' after upload), replace them
    if (url.includes('/upload/v')) {
      return url.replace('/upload/', `/upload/${transformations}/`);
    }
    
    // If URL has other transformations, replace them
    const uploadIndex = url.indexOf('/upload/') + 8;
    const nextSlashIndex = url.indexOf('/', uploadIndex);
    
    if (nextSlashIndex > uploadIndex) {
      // Has existing transformations
      return url.substring(0, uploadIndex) + transformations + url.substring(nextSlashIndex);
    }
    
    // No transformations, add them
    return url.replace('/upload/', `/upload/${transformations}/`);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  // Get inventory for a specific storage
  const getInventoryForStorage = (storageId) => {
    if (!product?.inventory) return 0;
    const inv = product.inventory.find(i => i.StorageID === storageId);
    return inv?.available_quantity || 0;
  };

  // Calculate total inventory
  const getTotalInventory = () => {
    if (!product?.inventory) return 0;
    return product.inventory.reduce((sum, inv) => sum + (inv.available_quantity || 0), 0);
  };

  // Handlers for barcode acceleration actions on ProductDetailPage.
  const handleGenerateBarcode = async () => {
    setFormError('');
    setFormSuccess('');
    setBarcodeBusy(true);
    try {
      const { data } = await API.post('/barcode/generate/', {
        ProductID: product.ProductID,
      });
      setFormSuccess('ברקוד נוצר');
      // Patch local form and original data so the new value is visible
      // immediately, and refresh the product query.
      setFormData((prev) => ({ ...prev, Barcode: data.Barcode }));
      setOriginalData((prev) => ({ ...prev, Barcode: data.Barcode }));
      queryClient.invalidateQueries(['product', product_id]);
    } catch (e) {
      setFormError(e?.response?.data?.error || 'שגיאה ביצירת ברקוד');
    } finally {
      setBarcodeBusy(false);
      setRegenerateConfirmOpen(false);
    }
  };

  // Re-render the barcode SVG whenever the Barcode value changes and the
  // field is in "view mode" (the svg ref is mounted only then).
  useEffect(() => {
    if (!barcodeSvgRef.current || !formData.Barcode) return;
    if (!isPrintableBarcode(formData.Barcode)) return;
    try {
      renderBarcodeSVG(barcodeSvgRef.current, formData.Barcode, {
        height: 50,
        fontSize: 12,
        preferredSymbology: barcodePrintSymbology,
      });
    } catch {
      // Pre-flight above guarantees this won't fire; silent guard.
    }
  }, [formData.Barcode, isEditing, barcodePrintSymbology]);

  // Render field based on its type and configuration
  const renderField = (fieldName) => {
    const fieldSchema = productFieldsSchema[fieldName] || {};
    const label = fieldSchema.label || fieldName;
    const fieldType = fieldSchema.type || 'string';
    const isReadonly = fieldSchema.readonly || false;

    if (isReadonly) {
      return null;
    }

    // ── Barcode field: special rendering ────────────────────────────
    // View mode: show an SVG barcode card with Print / Regenerate /
    // Generate buttons (gated on barcode_config.enabled).
    // Edit mode: standard TextField with a "Generate" endAdornment.
    if (fieldName === 'Barcode') {
      const currentBarcode = formData.Barcode || '';
      if (isEditing) {
        return (
          <Grid item xs={12} key={fieldName}>
            <Paper elevation={2} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {label || 'ברקוד'}:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <TextField
                    name="Barcode"
                    fullWidth
                    size="small"
                    value={currentBarcode}
                    onChange={handleInputChange}
                    placeholder="הזן ברקוד או לחץ על הכפתור לייצר"
                    label="ערך חדש"
                    InputProps={{
                      endAdornment: barcodeEnabled ? (
                        <InputAdornment position="end">
                          <Tooltip title="צור ברקוד ייחודי">
                            <span>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={handleGenerateBarcode}
                                disabled={barcodeBusy}
                                aria-label="צור ברקוד"
                              >
                                {barcodeBusy ? <CircularProgress size={18} /> : <AutoFixHighIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        );
      }
      // View mode
      return (
        <Grid item xs={12} key={fieldName}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'transparent' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {label || 'ברקוד'}:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                {currentBarcode ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: { xs: 0, sm: 200 },
                        direction: 'ltr',
                      }}
                    >
                      {isPrintableBarcode(currentBarcode) ? (
                        <svg ref={barcodeSvgRef} />
                      ) : (
                        <Box sx={{ p: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontWeight: 600, mb: 0.5 }}
                          >
                            {currentBarcode}
                          </Typography>
                          <Typography variant="caption" color="error" sx={{ direction: 'rtl' }}>
                            {UNSUPPORTED_BARCODE_MSG}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {barcodeEnabled && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="הדפס מדבקה">
                          <span>
                            <IconButton
                              color="primary"
                              onClick={() => setBarcodePrintOpen(true)}
                              disabled={!isPrintableBarcode(currentBarcode)}
                            >
                              <PrintIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="צור מחדש">
                          <IconButton
                            color="warning"
                            onClick={() => setRegenerateConfirmOpen(true)}
                            disabled={barcodeBusy}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      border: '2px dashed #e0e0e0',
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                      אין ברקוד למוצר זה
                    </Typography>
                    {barcodeEnabled && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AutoFixHighIcon />}
                        onClick={handleGenerateBarcode}
                        disabled={barcodeBusy}
                      >
                        {barcodeBusy ? 'יוצר...' : 'צור ברקוד'}
                      </Button>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      );
    }
    
    const fieldValue = formData[fieldName] || '';
    const originalValue = originalData[fieldName] || '';
    const hasChanged = fieldValue !== originalValue;
    
    let displayLabel = label;
    if (fieldName === 'ProductName') displayLabel = fieldSchema.label || `שם ${productLabel}`;
    if (fieldName === 'Price') displayLabel = fieldSchema.label || 'מחיר';
    if (fieldName === 'MinPrice') displayLabel = fieldSchema.label || 'מחיר מינימום';
    if (fieldName === 'CostPrice') displayLabel = fieldSchema.label || 'מחיר עלות';
    if (fieldName === 'Description') displayLabel = fieldSchema.label || 'תיאור';
    
    return (
      <Grid item xs={12} key={fieldName}>
        <Paper 
          elevation={isEditing ? 2 : 0} 
          sx={{ 
            p: 2, 
            transition: 'all 0.3s ease', 
            bgcolor: isEditing ? 'background.paper' : 'transparent',
            border: isEditing ? '1px solid #e0e0e0' : 'none'
          }}
        >
          <Grid container spacing={2} alignItems={fieldType === 'text' || fieldName === 'Description' ? "flex-start" : "center"}>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle1" fontWeight="bold">
                {displayLabel}:
              </Typography>
            </Grid>
            
            {isEditing ? (
              <>
                {fieldName !== 'Description' && fieldType !== 'text' && (
                  <Grid item xs={12} sm={4}>
                    <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      <strong>ערך נוכחי:</strong> {
                        fieldType === 'boolean' ? (originalValue ? 'כן' : 'לא') :
                        fieldType === 'number' && fieldName === 'Price' ? 
                          new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(parseFloat(originalValue) || 0) :
                        originalValue || 'לא הוגדר'
                      }
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12} sm={fieldName === 'Description' || fieldType === 'text' ? 9 : 5}>
                  <Box sx={{ display: 'flex', alignItems: fieldType === 'text' || fieldName === 'Description' ? 'flex-start' : 'center' }}>
                    {fieldType === 'boolean' ? (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={fieldValue === true || fieldValue === 'true'}
                            onChange={(e) => handleSwitchChange(fieldName, e.target.checked)}
                            name={fieldName}
                          />
                        }
                        label={fieldValue === true || fieldValue === 'true' ? 'כן' : 'לא'}
                      />
                    ) : fieldType === 'select' ? (
                      <TextField
                        name={fieldName}
                        fullWidth
                        value={fieldValue}
                        onChange={handleInputChange}
                        select
                        variant="outlined"
                        size="small"
                        label="ערך חדש"
                      >
                        <MenuItem value="">
                          <em>בחר</em>
                        </MenuItem>
                        {fieldSchema.options?.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : fieldType === 'text' || fieldName === 'Description' ? (
                      <TextField
                        name={fieldName}
                        fullWidth
                        value={fieldValue}
                        onChange={handleInputChange}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                        label="ערך חדש"
                      />
                    ) : fieldType === 'number' || fieldName === 'Price' ? (
                      <TextField
                        name={fieldName}
                        fullWidth
                        value={fieldValue}
                        onChange={handleInputChange}
                        onFocus={(e) => e.target.select()}
                        onWheel={(e) => e.target.blur()}
                        type="number"
                        variant="outlined"
                        size="small"
                        InputProps={{
                          inputProps: {
                            min: fieldSchema.min || 0,
                            step: 0.01
                          }
                        }}
                        error={fieldSchema.required && !fieldValue}
                        helperText={fieldSchema.required && !fieldValue ? 'שדה חובה' : ''}
                        label="ערך חדש"
                      />
                    ) : fieldType === 'date' ? (
                      <TextField
                        name={fieldName}
                        fullWidth
                        value={fieldValue}
                        onChange={handleInputChange}
                        type="date"
                        variant="outlined"
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="ערך חדש"
                      />
                    ) : (
                      <TextField
                        name={fieldName}
                        fullWidth
                        value={fieldValue}
                        onChange={handleInputChange}
                        variant="outlined"
                        size="small"
                        error={fieldSchema.required && !fieldValue}
                        helperText={fieldSchema.required && !fieldValue ? 'שדה חובה' : ''}
                        label="ערך חדש"
                      />
                    )}
                    
                    <Tooltip title="שחזר ערך מקורי">
                      <IconButton 
                        size="small" 
                        onClick={() => handleResetField(fieldName)}
                        disabled={!hasChanged}
                        sx={{ ml: 1 }}
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={9}>
                <Typography>
                  {fieldType === 'boolean' ? (fieldValue === true || fieldValue === 'true' ? 'כן' : 'לא') :
                   fieldType === 'number' && fieldName === 'Price' ? 
                    new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(parseFloat(fieldValue) || 0) :
                   fieldType === 'date' && fieldValue ? new Date(fieldValue).toLocaleDateString('he-IL') :
                   fieldValue || 'לא הוגדר'}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    );
  };

  if (isLoading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          {error?.response?.data?.error || `שגיאה בטעינת פרטי ה${productLabel}`}
        </Alert>
      </Container>
    );
  }

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
  const fieldsToDisplay = getFieldsInOrder();

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
        <div dir="rtl">
          <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, sm: 2 }, my: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1">
                פרטי {productLabel}
              </Typography>
              <Box>
                <Button 
                  variant="outlined" 
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/products')} 
                  sx={{direction: 'ltr', mr: 1}}
                >
                  חזרה לרשימת ה{productPluralLabel}
                </Button>
              </Box>
            </Box>

            {/* Product Details and Basic Inventory - Right aligned with max width */}
            <Box sx={{ 
              maxWidth: '1200px',
              mr: 0,
              ml: 'auto',
              mb: 4
            }}>
              {/* Product Details Card */}
              <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
                <CardHeader
                  title={<Typography sx={{ fontWeight: 'bold' }}>{product?.ProductName}</Typography>}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                  }}
                />
                <CardContent>
                  {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                  {formSuccess && <Alert severity="success" sx={{ mb: 2 }}>{formSuccess}</Alert>}

                  <Grid container spacing={3}>
                    {fieldsToDisplay.map((fieldName, index) => (
                      <React.Fragment key={fieldName}>
                        {renderField(fieldName)}
                        {index < fieldsToDisplay.length - 1 && (
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>
                        )}
                      </React.Fragment>
                    ))}

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                        {isEditing ? (
                          <>
                            <Button 
                              variant="outlined" 
                              color="secondary"
                              startIcon={<UndoIcon />}
                              onClick={handleResetAllFields}
                              disabled={!hasChanges}
                              sx={{direction: 'ltr'}}
                            >
                              שחזר הכל
                            </Button>
                            <Button 
                              variant="outlined" 
                              color="inherit"
                              startIcon={<CancelIcon />}
                              onClick={handleCancelEdit}
                              sx={{direction: 'ltr'}}
                            >
                              ביטול
                            </Button>
                            <Button 
                              variant="contained" 
                              color="primary"
                              startIcon={<SaveIcon />}
                              onClick={handleSubmit}
                              disabled={updateProductMutation.isLoading || !hasChanges}
                              sx={{direction: 'ltr'}}
                            >
                              {updateProductMutation.isLoading ? 'שומר...' : 'שמור שינויים'}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button 
                              variant="outlined" 
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={handleDelete}
                              disabled={deleteProductMutation.isLoading}
                              sx={{direction: 'ltr'}}
                            >
                              {deleteProductMutation.isLoading ? 'מוחק...' : `מחק ${productLabel}`}
                            </Button>
                            <Button 
                              variant="contained" 
                              color="primary"
                              startIcon={<EditIcon />}
                              onClick={handleStartEditing}
                              sx={{direction: 'ltr'}}
                            >
                              ערוך {productLabel}
                            </Button>
                          </>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
  
              {imagesEnabled && product && (
                <>
                <Divider sx={{ my: 4 }} />
                  {/* Upload Button - Above the card */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 2 
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      תמונות המוצר
                    </Typography>
                    <CloudinaryUploadWidget
                      productId={product_id}
                      onUploadSuccess={() => {
                        queryClient.invalidateQueries(['product', product_id]);
                        queryClient.invalidateQueries(['products']);
                      }}
                    />
                  </Box>
                  
                  {/* Image Card - Without upload button in header */}
                  <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
                    <CardHeader
                      title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhotoLibraryIcon />
                          <Typography sx={{ fontWeight: 'bold' }}>
                            {product.images?.length > 0 ? 
                              `${product.images.length} תמונות` : 
                              'אין תמונות'
                            }
                          </Typography>
                        </Box>
                      }
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                      }}
                    />
                  <CardContent>
                    {product.images && product.images.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {product.images.map((image) => (
                          <Box 
                            key={image.id} 
                            sx={{ 
                              position: 'relative',
                              border: image.is_primary ? '3px solid #1976d2' : '1px solid #ddd',
                              borderRadius: 2,
                              overflow: 'hidden',
                              transition: 'all 0.3s',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: 3
                              }
                            }}
                          >
                            <img
                              src={image.thumbnail_url}
                              alt={image.name || ''}
                              style={{ 
                                width: 150, 
                                height: 150, 
                                objectFit: 'cover',
                                cursor: 'pointer',
                                display: 'block'
                              }}
                              onClick={() => handleImageClick(image)}  // Changed from window.open
                            />
                            
                            {image.name && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  backgroundColor: '#f5f5f5'
                                }}
                              >
                                {image.name}
                              </Typography>
                            )}
                            
                            <Box sx={{
                              position: 'absolute',
                              bottom: image.name ? 30 : 0,
                              left: 0,
                              right: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: 1,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              p: 0.5
                            }}>
                              {image.is_primary && (
                                <Chip 
                                  label="ראשית" 
                                  size="small"
                                  sx={{ 
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    fontSize: '0.65rem',
                                    height: 20
                                  }}
                                />
                              )}
                              {!image.is_primary && (
                                <Tooltip title="הגדר כתמונה ראשית">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetPrimary(image.id);
                                    }}
                                    sx={{ color: 'white', p: 0.5 }}
                                  >
                                    <StarIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="הורד תמונה">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDownloadImage(image, e)}
                                  sx={{ color: 'white', p: 0.5 }}
                                >
                                  <DownloadIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="מחק תמונה">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setImageToDelete(image);
                                    setDeleteImageDialogOpen(true);
                                  }}
                                  sx={{ color: 'white', p: 0.5 }}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ 
                        py: 4, 
                        textAlign: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 2
                      }}>
                        <PhotoLibraryIcon sx={{ fontSize: 60, color: 'action.disabled' }} />
                        <Typography color="text.secondary" sx={{ mt: 2 }}>
                          אין תמונות למוצר זה
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </>
              )}

              {/* ADD THESE TWO DIALOGS RIGHT HERE - After the Image Card but before Inventory Card */}
              {imagesEnabled && (
                <MobileDialog
                  open={lightboxOpen}
                  onClose={() => {
                    setLightboxOpen(false);
                    setSelectedImage(null);
                  }}
                  maxWidth={false}
                  PaperProps={{
                    sx: { 
                      backgroundColor: 'rgba(0, 0, 0, 0.95)',
                      margin: 2,
                      maxWidth: '95vw',
                      maxHeight: '95vh',
                      minHeight: '50vh',
                      minWidth: '50vw'
                    }
                  }}
                >
                  <IconButton
                    onClick={() => setLightboxOpen(false)}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      zIndex: 1,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)'
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  
                  <DialogContent 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'transparent',
                      p: { xs: 0, sm: 2 },
                      position: 'relative',
                      minHeight: '400px'
                    }}
                  >
                    {selectedImage && (
                      <>
                        <Box sx={{
                          position: 'absolute',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2
                        }}>
                          <CircularProgress 
                            size={60}
                            thickness={2}
                            sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                          />
                          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                            טוען תמונה...
                          </Typography>
                        </Box>
                        
                        <img
                          src={getOptimizedImageUrl(selectedImage.display_url || selectedImage.url)}
                          alt={selectedImage.name || ''}
                          style={{ 
                            maxWidth: '100%',
                            maxHeight: '85vh',
                            objectFit: 'contain',
                            display: 'block',
                            opacity: 0,
                            transition: 'opacity 0.3s ease-in-out'
                          }}
                          onLoad={(e) => {
                            e.target.style.opacity = '1';
                            const loader = e.target.previousSibling;
                            if (loader) loader.style.display = 'none';
                          }}
                        />
                      </>
                    )}
                  </DialogContent>
                </MobileDialog>
              )}

              {imagesEnabled && (
                <MobileDialog 
                  open={deleteImageDialogOpen} 
                  onClose={() => {
                    setDeleteImageDialogOpen(false);
                    setImageToDelete(null);
                  }} 
                  maxWidth="sm" 
                  fullWidth
                >
                  <DialogTitle>מחיקת תמונה</DialogTitle>
                  <DialogContent>
                    {imageToDelete && (
                      <>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          האם אתה בטוח שברצונך למחוק את התמונה?
                        </Typography>
                        {imageToDelete.is_primary && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            זוהי התמונה הראשית של המוצר.
                          </Alert>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <img
                            src={imageToDelete.thumbnail_url}
                            alt=""
                            style={{ 
                              width: 150, 
                              height: 150, 
                              objectFit: 'cover',
                              border: '1px solid #ddd',
                              borderRadius: 8
                            }}
                          />
                        </Box>
                      </>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button 
                      onClick={() => {
                        setDeleteImageDialogOpen(false);
                        setImageToDelete(null);
                      }}
                    >
                      ביטול
                    </Button>
                    <Button 
                      onClick={async () => {
                        if (imageToDelete) {
                          await handleDeleteImage(imageToDelete.id);
                          setDeleteImageDialogOpen(false);
                          setImageToDelete(null);
                        }
                      }}
                      color="error"
                      variant="contained"
                    >
                      מחק
                    </Button>
                  </DialogActions>
                </MobileDialog>
              )}
  
              {/* Basic Inventory Management Card */}
              {storageEnabled && (
                <>
                <Divider sx={{ my: 4 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  ניהול מלאי
                </Typography>
                <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                  <CardHeader
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InventoryIcon />
                        <Typography sx={{ fontWeight: 'bold' }}>ניהול מלאי</Typography>
                      </Box>
                    }
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                    }}
                  />
                  <CardContent>
                    {isLoadingStorages ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : storages.length === 0 ? (
                      <Alert severity="info">
                        אין מחסנים במערכת. יש להוסיף מחסן כדי לנהל מלאי.
                      </Alert>
                    ) : (
                      <>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          mb: 3 
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            סה"כ מלאי:
                          </Typography>
                          <Paper
                            elevation={0}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 3,
                              py: 1,
                              backgroundColor: getTotalInventory() > 0 ? '#e8f5e9' : '#ffebee',
                              border: '3px solid',
                              borderColor: getTotalInventory() > 0 ? '#4caf50' : '#f44336',
                              borderRadius: 2,
                            }}
                          >
                            <Typography 
                              variant="h5" 
                              sx={{ 
                                fontWeight: 'bold',
                                color: getTotalInventory() > 0 ? '#2e7d32' : '#c62828',
                              }}
                            >
                              {getTotalInventory()}
                            </Typography>
                          </Paper>
                        </Box>

                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">מחסן</TableCell>
                                <TableCell align="center">כמות במלאי</TableCell>
                                {canUpdateInventoryFull && (
                                  <TableCell align="center">פעולות</TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {storages.map((storage) => {
                                const quantity = getInventoryForStorage(storage.StorageID);
                                return (
                                  <TableRow key={storage.StorageID}>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {storage.StorageName}
                                        {storage.is_default && (
                                          <Chip 
                                            icon={<HomeIcon sx={{ fontSize: 16 }} />}
                                            size="small" 
                                            label="ברירת מחדל" 
                                            color="primary" 
                                            sx={{ direction: 'ltr'}}
                                          />
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Paper
                                        elevation={0}
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          px: 2,
                                          py: 0.5,
                                          backgroundColor: quantity > 0 ? '#e8f5e9' : '#ffebee',
                                          border: '2px solid',
                                          borderColor: quantity > 0 ? '#4caf50' : '#f44336',
                                          borderRadius: 2,
                                        }}
                                      >
                                        <Typography 
                                          variant="body1" 
                                          sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem',
                                            color: quantity > 0 ? '#2e7d32' : '#c62828',
                                          }}
                                        >
                                          {quantity}
                                        </Typography>
                                      </Paper>
                                    </TableCell>
                                    {canUpdateInventoryFull && (
                                      <TableCell align="center">
                                        <Tooltip title="עדכן מלאי" arrow>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleInventoryEdit(storage, quantity)}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </CardContent>
                </Card>
                </>
              )}
            </Box>

            {/* Inventory Audit History Section - Full Width */}
            {storageEnabled && (
              <>
                <Divider sx={{ my: 4 }} />
                
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  היסטוריית מלאי
                </Typography>

                <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4, width: '100%' }}>
                  <CardHeader
                    title={
                      <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                        תנועות מלאי
                      </Typography>
                    }
                    sx={{
                      backgroundColor: 'secondary.main',
                      color: 'white',
                      py: 1.5,
                    }}
                  />
                  <CardContent> 
                    {/* Filters */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: 2, 
                      alignItems: 'center',
                      mb: 2 
                    }}>
                      <TextField
                        label="סוג שינוי"
                        select
                        size="small"
                        value={auditFilters.changeType}
                        onChange={(e) => setAuditFilters(prev => ({ ...prev, changeType: e.target.value }))}
                        sx={{ width: 180 }}
                      >
                        <MenuItem value="All">הכל</MenuItem>
                        <MenuItem value="addition">הוספות</MenuItem>
                        <MenuItem value="reduction">הפחתות</MenuItem>
                      </TextField>
                      
                      {/* ADD THIS - Storage Filter */}
                      <TextField
                        label="מחסן"
                        select
                        size="small"
                        value={auditFilters.storageId}
                        onChange={(e) => setAuditFilters(prev => ({ ...prev, storageId: e.target.value }))}
                        sx={{ width: 180 }}
                      >
                        <MenuItem value="All">כל המחסנים</MenuItem>
                        {storages.map(storage => (
                          <MenuItem key={storage.StorageID} value={storage.StorageID}>
                            {storage.StorageName}
                            {storage.is_default && ' (ברירת מחדל)'}
                          </MenuItem>
                        ))}
                      </TextField>
                      
                      <DatePicker
                        label="חודש"
                        value={auditFilters.startDate}
                        onChange={(newValue) => {
                          if (newValue) {
                            setAuditFilters(prev => ({ ...prev, startDate: startOfMonth(newValue), endDate: endOfMonth(newValue) }));
                          }
                        }}
                        views={['month', 'year']}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { width: 160 },
                          },
                        }}
                      />

                      <DatePicker
                        label="מתאריך"
                        value={auditFilters.startDate}
                        onChange={(newValue) => setAuditFilters(prev => ({ ...prev, startDate: newValue }))}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { width: 160 },
                          },
                        }}
                      />

                      <DatePicker
                        label="עד תאריך"
                        value={auditFilters.endDate}
                        onChange={(newValue) => setAuditFilters(prev => ({ ...prev, endDate: newValue }))}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { width: 160 },
                          },
                        }}
                      />
                      
                      <TextField
                        label="חיפוש"
                        size="small"
                        value={auditFilters.search}
                        onChange={(e) => setAuditFilters(prev => ({ ...prev, search: e.target.value }))}
                        placeholder="חפש בסיבה או הערות"
                        sx={{ width: 200 }}
                      />
                      
                      <Button
                        variant="contained"
                        size="large"
                        sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                        onClick={() => {
                          setAuditPaginationModel({
                            pageSize: 25,
                            page: 0,
                          });
                          fetchAuditHistory();
                        }}
                      >
                        סינון
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {/* Audit history table */}
                    {loadingAudit ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                        <Box sx={{
                          width: '100%',
                          '.MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            whiteSpace: 'normal !important',
                            lineHeight: '1.2',
                            overflow: 'visible',
                          },
                          '.MuiDataGrid-cell': {
                            fontSize: '1.1rem',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          },
                        }}>
                        <DataGridPro
                          rows={auditHistory}
                          columns={auditColumns}
                          getRowId={(row) => row.AuditID}
                          density="comfortable"
                          autoHeight
                          pagination
                          slots={{
                            toolbar: CustomAuditToolbar,
                          }}
                          paginationMode="server"
                          rowCount={auditRowCount}
                          pageSizeOptions={[25, 50, 100]}
                          paginationModel={auditPaginationModel}
                          onPaginationModelChange={setAuditPaginationModel}
                          initialState={{
                            sorting: {
                              sortModel: [{ field: 'Timestamp', sort: 'desc' }],
                            },
                          }}
                          disableSelectionOnClick
                          localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                          getRowClassName={(params) => {
                            const changeType = params.row?.ChangeType;
                            return changeType === 'addition' ? 'addition-row' : 'reduction-row';
                          }}
                          sx={{
                            '& .addition-row': {
                              backgroundColor: '#e8f5e9',
                              '&:hover': {
                                backgroundColor: '#c8e6c9',
                              },
                            },
                            '& .reduction-row': {
                              backgroundColor: '#ffebee',
                              '&:hover': {
                                backgroundColor: '#ffcdd2',
                              },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Summary below the table - Similar style to TransactionsPage */}
                {!loadingAudit && auditHistory.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'right' }}>
                      סיכום סינון נוכחי:
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-start',
                      alignItems: 'center', 
                      gap: 3,
                      flexWrap: 'wrap' 
                    }}>
                      {/* Additions Summary */}
                      <Box sx={{ 
                        backgroundColor: '#e8f5e9',
                        padding: '8px 16px',
                        borderRadius: 2,
                        border: '1px solid #c8e6c9',
                        display: 'inline-block',
                      }}>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                          סך הוספות:{' '}
                          <Typography component="span" sx={{ fontWeight: 'bold', direction: 'rtl' }}>
                           {auditSummary.total_additions || 0}+
                          </Typography>
                        </Typography>
                      </Box>

                      {/* Reductions Summary */}
                      <Box sx={{ 
                        backgroundColor: '#ffebee',
                        padding: '8px 16px',
                        borderRadius: 2,
                        border: '1px solid #ffcdd2',
                        display: 'inline-block'
                      }}>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                          סך הפחתות:{' '}
                          <Typography component="span" sx={{ fontWeight: 'bold' }}>
                            {auditSummary.total_reductions || 0}-
                          </Typography>
                        </Typography>
                      </Box>

                      {/* Net Change Summary */}
                      <Box sx={{ 
                        backgroundColor: '#f5f5f5',
                        padding: '8px 16px',
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        display: 'inline-block'
                      }}>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                          שינוי נטו:{' '}
                          <Typography component="span" sx={{ fontWeight: 'bold' }}>
                            {((auditSummary.total_additions || 0) - (auditSummary.total_reductions || 0)).toFixed(2)}
                          </Typography>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* Delete Confirmation Dialog */}
            <MobileDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
              <DialogTitle>מחיקת {productLabel}</DialogTitle>
              <DialogContent>
                <Typography>
                  האם אתה בטוח שברצונך למחוק את ה{productLabel} "{product?.ProductName}"?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  פעולה זו אינה ניתנת לביטול.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)}>ביטול</Button>
                <Button 
                  onClick={handleConfirmDelete} 
                  color="error" 
                  variant="contained"
                  disabled={deleteProductMutation.isLoading}
                >
                  {deleteProductMutation.isLoading ? 'מוחק...' : 'מחק'}
                </Button>
              </DialogActions>
            </MobileDialog>

            {/* Inventory Management Dialog — same add/take data-grid UX
                used on ProductsPage. Replaces the old "set absolute amount"
                dialog so all manual adjustments go through one pipeline. */}
            <InventoryManagementDialog
              open={inventoryDialogOpen}
              onClose={() => setInventoryDialogOpen(false)}
              product={product}
              storages={storages}
              initialStorageId={selectedStorage?.StorageID || null}
              onSuccess={handleInventorySuccess}
              canTakeInventory={canTakeInventory}
              canAddInventory={canAddInventory}
            />

            {/* Regenerate-barcode confirm dialog */}
            <MobileDialog
              open={regenerateConfirmOpen}
              onClose={() => setRegenerateConfirmOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>צור ברקוד מחדש</DialogTitle>
              <DialogContent>
                <Typography>
                  פעולה זו תחליף את הברקוד הקיים ({formData.Barcode}) בברקוד חדש. האם להמשיך?
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRegenerateConfirmOpen(false)} disabled={barcodeBusy}>
                  ביטול
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleGenerateBarcode}
                  disabled={barcodeBusy}
                >
                  {barcodeBusy ? 'יוצר...' : 'צור מחדש'}
                </Button>
              </DialogActions>
            </MobileDialog>

            {/* Barcode Print Dialog */}
            {barcodeEnabled && (
              <BarcodePrintDialog
                open={barcodePrintOpen}
                onClose={() => setBarcodePrintOpen(false)}
                initialProducts={product ? [product] : []}
              />
            )}
          </Container>
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default ProductDetailPage;